import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const icons = ['ri-table-line', 'ri-team-line', 'ri-star-line', 'ri-truck-line', 'ri-coupon-2-line', 'ri-shield-check-line'];
const targets = ['#spreadsheet', '#categories', '/article/usfans-review', '/article/usfans-shipping-cost', '/article/usfans-coupon', '/article/usfans-safety'];

const images = [
  'https://readdy.ai/api/search-image?query=A%20clean%20tablet%20screen%20showing%20an%20organized%20spreadsheet%20with%20rows%20and%20columns%20on%20a%20minimal%20cream%20desk%20with%20a%20small%20plant%20and%20sage%20green%20accents%20gentle%20natural%20daylight%20editorial%20photography%20subtle%20shadows%20high%20detail&width=600&height=400&seq=val-spreadsheet&orientation=landscape',
  'https://readdy.ai/api/search-image?query=A%20clean%20grid%20of%20minimal%20profile%20cards%20and%20avatar%20placeholders%20with%20sage%20green%20and%20cream%20tones%20on%20a%20soft%20light%20background%20gentle%20daylight%20editorial%20photography%20subtle%20shadows%20organized%20clean%20aesthetic%20high%20detail&width=600&height=400&seq=val-directory&orientation=landscape',
  'https://readdy.ai/api/search-image?query=A%20minimal%20desk%20scene%20with%20an%20open%20notebook%20a%20smartphone%20showing%20five%20star%20ratings%20and%20a%20small%20cup%20of%20coffee%20in%20warm%20neutral%20tones%20gentle%20daylight%20editorial%20photography%20subtle%20shadows%20high%20detail&width=600&height=400&seq=val-reviews&orientation=landscape',
  'https://readdy.ai/api/search-image?query=A%20neat%20stack%20of%20brown%20cardboard%20shipping%20boxes%20with%20packing%20tape%20on%20a%20clean%20cream%20surface%20with%20gentle%20natural%20daylight%20editorial%20product%20photography%20subtle%20soft%20shadows%20warm%20neutral%20tones%20high%20detail&width=600&height=400&seq=val-shipping&orientation=landscape',
  'https://readdy.ai/api/search-image?query=A%20few%20paper%20discount%20vouchers%20and%20gift%20cards%20in%20sage%20green%20and%20warm%20amber%20tones%20with%20a%20small%20price%20tag%20on%20a%20clean%20cream%20surface%20gentle%20daylight%20editorial%20photography%20subtle%20shadows%20high%20detail&width=600&height=400&seq=val-coupons&orientation=landscape',
  'https://readdy.ai/api/search-image?query=A%20minimal%20security%20concept%20with%20a%20small%20padlock%20icon%20and%20a%20shield%20symbol%20in%20sage%20green%20and%20cream%20tones%20on%20a%20clean%20light%20background%20gentle%20daylight%20editorial%20photography%20subtle%20shadows%20calm%20trustworthy%20aesthetic%20high%20detail&width=600&height=400&seq=val-safety&orientation=landscape',
];

export default function ValueSection() {
  const { t } = useTranslation();

  const items = [
    { key: 'spreadsheet', icon: icons[0], target: targets[0], image: images[0] },
    { key: 'directory', icon: icons[1], target: targets[1], image: images[1] },
    { key: 'reviews', icon: icons[2], target: targets[2], image: images[2] },
    { key: 'shipping', icon: icons[3], target: targets[3], image: images[3] },
    { key: 'coupons', icon: icons[4], target: targets[4], image: images[4] },
    { key: 'safety', icon: icons[5], target: targets[5], image: images[5] },
  ];

  return (
    <section className="bg-cream">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-2xl font-bold text-stone-900 md:text-3xl">
            {t('value.title')}
          </h2>
          <p className="mt-3 text-sm text-stone-500 md:text-base">{t('value.subtitle')}</p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <Item
              key={item.key}
              icon={item.icon}
              target={item.target}
              image={item.image}
              title={t(`value.${item.key}.title`)}
              body={t(`value.${item.key}.body`)}
              alt={t(`value.${item.key}.title`)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function Item({
  icon,
  target,
  image,
  title,
  body,
  alt,
}: {
  icon: string;
  target: string;
  image: string;
  title: string;
  body: string;
  alt: string;
}) {
  const inner = (
    <>
      <div className="relative h-40 overflow-hidden">
        <img
          src={image}
          alt={alt}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-6">
        <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-50 text-xl text-emerald-600">
          <i className={icon}></i>
        </span>
        <h3 className="mt-4 font-heading text-base font-semibold text-stone-900 md:text-lg">
          {title}
        </h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-stone-500">{body}</p>
        <span className="mt-4 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-emerald-600">
          <i className="ri-arrow-right-line"></i>
        </span>
      </div>
    </>
  );

  const cardClass =
    'group flex flex-col overflow-hidden rounded-lg border border-stone-200 bg-white transition-colors hover:border-emerald-300';

  if (target.startsWith('#')) {
    return (
      <a href={target} className={cardClass}>
        {inner}
      </a>
    );
  }

  return (
    <Link to={target} className={cardClass}>
      {inner}
    </Link>
  );
}